import { useMemo, useState } from 'react';

const DOW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MON = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function isoOf(y: number, m: number, d: number) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function todayISO() {
  const d = new Date();
  return isoOf(d.getFullYear(), d.getMonth(), d.getDate());
}

function fmtLabel(iso: string) {
  if (!iso) return '';
  const y = iso.slice(0, 4);
  const m = +iso.slice(5, 7) - 1;
  const d = +iso.slice(8, 10);
  return `${d} ${MON[m].slice(0, 3)} ${y}`;
}

type Props = {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  /** Single date selection — no range preview or second click. */
  mode?: 'single' | 'range';
  /** ISO `YYYY-MM-DD` — dates before this are not selectable. */
  minDate?: string;
  /** ISO `YYYY-MM-DD` — dates after this are not selectable. */
  maxDate?: string;
};

function isDisabled(iso: string, minDate?: string, maxDate?: string): boolean {
  if (minDate && iso < minDate) return true;
  if (maxDate && iso > maxDate) return true;
  return false;
}

export function isDateRangeDayDisabled(iso: string, minDate?: string, maxDate?: string): boolean {
  return isDisabled(iso, minDate, maxDate);
}

export default function DateRangePicker({
  from,
  to,
  onChange,
  mode = 'range',
  minDate,
  maxDate,
}: Props) {
  const [viewYear, setViewYear] = useState(() => (from ? +from.slice(0, 4) : new Date().getFullYear()));
  const [viewMonth, setViewMonth] = useState(() => (from ? +from.slice(5, 7) - 1 : new Date().getMonth()));
  const [hovered, setHovered] = useState('');

  const today = todayISO();

  const cells = useMemo<(string | null)[]>(() => {
    const firstDow = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMon = new Date(viewYear, viewMonth + 1, 0).getDate();
    const out: (string | null)[] = Array(firstDow).fill(null);
    for (let d = 1; d <= daysInMon; d++) out.push(isoOf(viewYear, viewMonth, d));
    while (out.length % 7) out.push(null);
    return out;
  }, [viewYear, viewMonth]);

  const goMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y--;
    }
    if (m > 11) {
      m = 0;
      y++;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  const click = (iso: string) => {
    if (isDisabled(iso, minDate, maxDate)) return;
    if (mode === 'single') {
      if (iso === from) onChange('', '');
      else onChange(iso, '');
      return;
    }
    if (iso === from && !to) {
      onChange('', '');
      return;
    }
    if (!from || (from && to)) {
      onChange(iso, '');
      return;
    }
    if (iso < from) onChange(iso, from);
    else onChange(from, iso);
  };

  // Effective range including hover preview for mid-selection state.
  // When hovering left of `from`, the original `from` becomes the right endpoint.
  const prevFrom = mode === 'single' ? from : from && !to && hovered ? (hovered < from ? hovered : from) : from;
  const prevTo = mode === 'single' ? '' : from && !to && hovered ? (hovered >= from ? hovered : from) : to;

  const cellClass = (iso: string | null): string => {
    if (!iso) return 'drp-cell drp-cell--empty';
    const disabled = isDisabled(iso, minDate, maxDate);
    const f = prevFrom;
    const t = prevTo;
    const isStart = !!f && iso === f;
    const isEnd = !!t && iso === t;
    const hasRange = f && t && f !== t;
    const inRange = !!hasRange && iso > f && iso < t;
    const cls = ['drp-cell'];
    if (disabled) cls.push('drp-cell--disabled');
    if (iso === today) cls.push('drp-cell--today');
    if (inRange) cls.push('drp-cell--mid');
    if (isStart || isEnd) cls.push('drp-cell--sel');
    if (isStart && hasRange) cls.push('drp-cell--rstart');
    if (isEnd && hasRange) cls.push('drp-cell--rend');
    return cls.join(' ');
  };

  const hasSelection = from || to;
  const daysInViewMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfViewMonth = isoOf(viewYear, viewMonth, 1);
  const lastDayOfViewMonth = isoOf(viewYear, viewMonth, daysInViewMonth);
  const canGoPrev = !minDate || firstDayOfViewMonth > minDate;
  const canGoNext = !maxDate || lastDayOfViewMonth < maxDate;

  return (
    <div className="drp" onMouseLeave={() => mode !== 'single' && setHovered('')}>
      <div className="drp-nav">
        <button
          type="button"
          className="drp-nav-btn"
          onClick={() => goMonth(-1)}
          disabled={!canGoPrev}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="drp-nav-title">
          {MON[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          className="drp-nav-btn"
          onClick={() => goMonth(1)}
          disabled={!canGoNext}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="drp-grid">
        {DOW.map((d) => (
          <div key={d} className="drp-dow">
            {d}
          </div>
        ))}
        {cells.map((iso, i) => (
          <div
            key={i}
            className={cellClass(iso)}
            onClick={() => iso && !isDisabled(iso, minDate, maxDate) && click(iso)}
            onMouseEnter={() =>
              mode !== 'single' && iso && !isDisabled(iso, minDate, maxDate) && setHovered(iso)
            }
            role={iso && !isDisabled(iso, minDate, maxDate) ? 'button' : undefined}
            tabIndex={iso && !isDisabled(iso, minDate, maxDate) ? 0 : -1}
            onKeyDown={(e) => {
              if (iso && !isDisabled(iso, minDate, maxDate) && (e.key === 'Enter' || e.key === ' ')) click(iso);
            }}
            aria-label={iso ? fmtLabel(iso) : undefined}
            aria-disabled={iso ? isDisabled(iso, minDate, maxDate) : undefined}
            aria-pressed={mode === 'single' ? iso === from : iso === from || iso === to}
          >
            <span className="drp-day">{iso ? +iso.slice(8) : ''}</span>
          </div>
        ))}
      </div>

      {hasSelection ? (
        <div className="drp-footer">
          <span className="drp-range-lbl">
            {mode === 'single'
              ? fmtLabel(from)
              : `${fmtLabel(from)}${from && to ? ' → ' : ''}${to && to !== from ? fmtLabel(to) : ''}`}
          </span>
          <button type="button" className="drp-clear" onClick={() => onChange('', '')}>
            Clear
          </button>
        </div>
      ) : null}
    </div>
  );
}

